import { create } from 'zustand';
import apiClient from '../api/api';
import toast from 'react-hot-toast';

export const useAdminStore = create((set, get) => ({
  users: [],
  companies: [],
  stores: [],
  ads: [],
  officialMediaAssets: [],
  agoraContentConfig: { prioritizeOfficialContent: true },
  isLoading: false,
  isProcessing: false,
  error: null,
  hasFetched: false, // Nueva propiedad para rastrear si los datos ya se han cargado

  // --- ACTIONS ---

  fetchAdminData: async () => {
    set({ isLoading: true, error: null });
    try {
      const [usersRes, companiesRes, storesRes, adsRes] = await Promise.all([
        apiClient.get('/admin/users'),
        apiClient.get('/admin/companies'),
        apiClient.get('/admin/stores'),
        apiClient.get('/admin/ads'),
      ]);
      const adsPayload = adsRes.data;
      const ads = Array.isArray(adsPayload?.ads)
        ? adsPayload.ads
        : (Array.isArray(adsPayload) ? adsPayload : []);
      set({
        users: usersRes.data,
        companies: companiesRes.data,
        stores: storesRes.data,
        ads,
        isLoading: false,
        hasFetched: true, // Establecer a true después de una carga exitosa
      });
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudieron cargar los datos del panel.';
      set({ isLoading: false, error: message });
      toast.error(message);
    }
  },

  updatePageStatus: async (pageType, pageId, newStatus) => {
    try {
      const response = await apiClient.patch(
        `/admin/pages/${pageType}/${pageId}/status`,
        { status: newStatus }
      );
      toast.success('Estado actualizado con éxito.');

      const listKey = `${pageType}s`; // 'companies' or 'stores'
      set((state) => ({
        [listKey]: state[listKey].map((p) =>
          p._id === pageId ? response.data.page : p
        ),
      }));
    } catch (error) {
      toast.error('No se pudo actualizar el estado.');
    }
  },

  // 4. Nueva acción para actualizar el estado de un anuncio
  updateAdStatus: async (adId, newStatus) => {
    try {
      const response = await apiClient.put(
        `/admin/ads/${adId}/status`,
        { status: newStatus }
      );
      toast.success('Estado del anuncio actualizado.');

      // Actualizamos el anuncio en la lista local
      set((state) => ({
        ads: state.ads.map((ad) => (ad._id === adId ? response.data : ad)),
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo actualizar el estado del anuncio.');
    }
  },

  fetchOfficialMediaAssets: async () => {
    try {
      const [assetsRes, configRes] = await Promise.all([
        apiClient.get('/admin/official-media'),
        apiClient.get('/admin/agora-content-config'),
      ]);

      set({
        officialMediaAssets: Array.isArray(assetsRes.data) ? assetsRes.data : [],
        agoraContentConfig: configRes.data || { prioritizeOfficialContent: true },
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo cargar el stock oficial.');
    }
  },

  createOfficialMediaAsset: async (payload) => {
    try {
      const response = await apiClient.post('/admin/official-media', payload);
      set((state) => ({
        officialMediaAssets: [response.data, ...state.officialMediaAssets],
      }));
      toast.success('Asset oficial creado con éxito.');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo crear el asset oficial.';
      toast.error(message);
      throw error;
    }
  },

  updateOfficialMediaAsset: async (assetId, payload) => {
    try {
      const response = await apiClient.patch(`/admin/official-media/${assetId}`, payload);
      set((state) => ({
        officialMediaAssets: state.officialMediaAssets.map((asset) =>
          asset._id === assetId ? response.data : asset
        ),
      }));
      toast.success('Asset oficial actualizado.');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo actualizar el asset oficial.';
      toast.error(message);
      throw error;
    }
  },

  removeOfficialMediaAsset: async (assetId) => {
    try {
      await apiClient.delete(`/admin/official-media/${assetId}`);
      set((state) => ({
        officialMediaAssets: state.officialMediaAssets.map((asset) =>
          asset._id === assetId ? { ...asset, isActive: false } : asset
        ),
      }));
      toast.success('Asset oficial desactivado.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo desactivar el asset oficial.';
      toast.error(message);
    }
  },

  setAgoraPriorityConfig: async (prioritizeOfficialContent) => {
    try {
      const response = await apiClient.patch('/admin/agora-content-config', {
        prioritizeOfficialContent,
      });
      set({ agoraContentConfig: response.data || { prioritizeOfficialContent } });
      toast.success('Prioridad de contenido oficial actualizada.');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo actualizar la prioridad oficial.';
      toast.error(message);
      throw error;
    }
  },

  seedDatabase: async () => {
    if (window.confirm('¿Estás seguro de que quieres cargar los datos de prueba?')) {
      set({ isProcessing: true });
      try {
        await apiClient.post('/admin/seed');
        toast.success('Datos de prueba cargados con éxito.');
        await get().fetchAdminData(); // Refresh data
      } catch (error) {
        toast.error('No se pudieron cargar los datos de prueba.');
      } finally {
        set({ isProcessing: false });
      }
    }
  },

  clearTestData: async () => {
    if (window.confirm('¡ACCIÓN IRREVERSIBLE! ¿Estás seguro de que quieres eliminar todos los datos de prueba?')) {
      set({ isProcessing: true });
      try {
        await apiClient.delete('/admin/clear-test-data');
        toast.success('Datos de prueba eliminados con éxito.');
        await get().fetchAdminData(); // Refresh data
      } catch (error) {
        toast.error('No se pudieron eliminar los datos de prueba.');
      } finally {
        set({ isProcessing: false });
      }
    }
  },

  // 🟢 SOLUCIÓN: Acción para que el admin cree un anuncio
  createAd: async (adData) => {
    set({ isProcessing: true });
    try {
      // Los admins pueden crear anuncios directamente como 'activos'
      const response = await apiClient.post('/ads/create', { ...adData, status: 'active', paymentStatus: 'paid' });
      const createdAd = response.data?.data?.ad;
      set((state) => ({
        ads: createdAd ? [createdAd, ...state.ads] : state.ads,
        isProcessing: false,
      }));
      toast.success('Anuncio interno creado con éxito.');
      return createdAd;
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo crear el anuncio.';
      set({ isProcessing: false, error: message });
      toast.error(message);
      throw error;
    }
  },

  createAdminUser: async (userData) => {
    set({ isProcessing: true });
    try {
      const response = await apiClient.post('/admin/users', userData);
      set((state) => ({
        users: [response.data, ...state.users],
        isProcessing: false,
      }));
      toast.success('Usuario creado con éxito.');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo crear el usuario.';
      set({ isProcessing: false, error: message });
      toast.error(message);
      throw error; // Re-throw for the form to catch
    }
  },

  updateUserRole: async (userId, newRole) => {
    set({ isProcessing: true });
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/role`, { role: newRole });
      set((state) => ({
        users: state.users.map((u) => (u._id === userId ? response.data : u)),
        isProcessing: false,
      }));
      toast.success('Rol de usuario actualizado.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo actualizar el rol.';
      set({ isProcessing: false, error: message });
      toast.error(message);
    }
  },

  updateUserStatus: async (userId, newStatus) => {
    set({ isProcessing: true });
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/status`, { status: newStatus });
      set((state) => ({
        users: state.users.map((u) => (u._id === userId ? response.data : u)),
        isProcessing: false,
      }));
      toast.success('Estado de usuario actualizado.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo actualizar el estado.';
      set({ isProcessing: false, error: message });
      toast.error(message);
    }
  },

  deleteUser: async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario permanentemente? Esta acción es irreversible.')) {
      return;
    }
    set({ isProcessing: true });
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      set((state) => ({
        users: state.users.filter((u) => u._id !== userId),
        isProcessing: false,
      }));
      toast.success('Usuario eliminado con éxito.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo eliminar el usuario.';
      set({ isProcessing: false, error: message });
      toast.error(message);
    }
  },

  // 🟢 SOLUCIÓN: Nuevas acciones para eliminar tiendas y empresas
  deleteStore: async (storeId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta tienda permanentemente? Esta acción es irreversible.')) {
      return;
    }
    set({ isProcessing: true });
    try {
      await apiClient.delete(`/admin/stores/${storeId}`);
      set((state) => ({
        stores: state.stores.filter((s) => s._id !== storeId),
        isProcessing: false,
      }));
      toast.success('Tienda eliminada con éxito.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo eliminar la tienda.';
      set({ isProcessing: false, error: message });
      toast.error(message);
    }
  },

  deleteCompany: async (companyId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta empresa permanentemente? Esta acción es irreversible.')) {
      return;
    }
    set({ isProcessing: true });
    try {
      await apiClient.delete(`/admin/companies/${companyId}`);
      set((state) => ({
        companies: state.companies.filter((c) => c._id !== companyId),
        isProcessing: false,
      }));
      toast.success('Empresa eliminada con éxito.');
    } catch (error) {
      const message = error.response?.data?.message || 'No se pudo eliminar la empresa.';
      set({ isProcessing: false, error: message });
      toast.error(message);
    }
  },
}));